<template>
  <div
    v-on-long-press.stop="showContextMenu"
    class="flex-col rounded overflow-hidden items-center justify-center text-center border border-n-600 border-box h-20 sm:h-32"
    :class="{
      'border-v-300': selectedQuantity > 0,
      'opacity-60 pointer-events-none': disabled,
      'cursor-pointer': product.hasStock !== false
    }"
    @contextmenu.prevent="showContextMenu"
    @click="selectProduct"
  >
    <div
      class="font-heading text-xl sm:text-2xl font-bold w-full h-8 sm:h-16 bg-n-600 flex items-center justify-center relative"
      :style="{
        background: background,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderColor: borderColor
      }"
    >
      <div v-if="product.hasLowStock" class="flex gap-1 font-body text-sm font-medium text-y-500">
        <div>
          {{ t('stock.low-stock') }}
        </div>
        <div v-if="stockByOrganizationProductId[product.organizationItemId]?.stock">
          ({{ stockByOrganizationProductId[product.organizationItemId].stock }})
        </div>
      </div>
      <div v-else-if="product.hasStock === false" class="font-body text-sm font-medium text-r-500">
        {{ t('stock.out-of-stock') }}
      </div>
      <div
        v-else-if="!hasBackgroundImage"
        :style="{
          color: getTextColor(product.color)
        }"
        class="font-bold font-text uppercase"
      >
        {{ shortName }}
      </div>

      <l-badge
        v-if="selectedQuantity > 0"
        class="absolute top-2 right-2"
        size="large"
        mode="primary"
        :value="selectedQuantity"
      />
    </div>
    <div
      class="bg-n-700 h-12 sm:h-16 flex items-center justify-center w-full text-center text-n-0 font-body text-xs sm:text-sm"
    >
      <div class="line-clamp-3">
        {{ product.name }}
      </div>
    </div>
    <quantity-selector-modal
      :visible="showQuantitySelector"
      :label="product.name"
      @close="hideContextMenu"
      @execute="
        quantity => {
          $emit('selected', product, quantity)
          showQuantitySelector = false
        }
      "
    />
  </div>
</template>

<script setup lang="ts">
import { vOnLongPress } from '@vueuse/components'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { darken } from '@last/core'
import { LBadge } from '@last/core-ui/paprika'

import images from '@/images'
import QuantitySelectorModal from '@/modules/ordering/QuantitySelectorModal.vue'
import { useCatalogStore } from '@/store/catalog'
import { useConfigStore } from '@/store/config'
import type { CatalogProduct } from '@/types'

const props = withDefaults(
  defineProps<{
    product: CatalogProduct
    componentStyle: string
    selectedQuantity: number
    disabled?: boolean
  }>(),
  {
    componentStyle: 'square',
    selectedQuantity: 0,
    disabled: false
  }
)

const emit = defineEmits<{
  selected: [CatalogProduct, number]
}>()

const { t } = useI18n()
const configStore = useConfigStore()
const { config } = storeToRefs(configStore)
const catalogStore = useCatalogStore()
const { stockByOrganizationProductId } = storeToRefs(catalogStore)
const showQuantitySelector = ref<boolean>(false)

const shortName = computed(() => {
  if (props.product.shortName) {
    return props.product.shortName
  }
  if (!props.product.name) return ''
  const initials = props.product.name
    .split(/[.,/ -]/)
    .map(n => n[0])
    .join('')
    .slice(0, 2)
  if (initials.length < 2) {
    return props.product.name.slice(0, 2)
  } else {
    return initials
  }
})

const background = computed(() => {
  const { hasStock, hasLowStock, imageId, color } = props.product
  if (hasStock === false) {
    return '#FFE0E0'
  }
  if (hasLowStock) {
    return '#FFF9E5'
  }
  if (config.value.showProductImages && !!imageId) {
    const image = images.generateImageUrl(imageId)
    return `url(${image})`
  } else {
    return color || '#3E3E61'
  }
})

const hasBackgroundImage = computed(() => {
  return background.value.includes('url(', 0)
})

const borderColor = computed(() => {
  if (!props.product.color) return '#3E3E61'
  return darken(props.product.color)
})

function getTextColor(hexcolor: string | null) {
  if (!hexcolor) return 'white'
  const color = hexcolor.substr(1, 6)
  const r = parseInt(color.substr(0, 2), 16)
  const g = parseInt(color.substr(2, 2), 16)
  const b = parseInt(color.substr(4, 2), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 200 ? '#302F66' : 'white'
}

function showContextMenu(): void {
  showQuantitySelector.value = true
}

function hideContextMenu(): void {
  showQuantitySelector.value = false
}

function selectProduct(): void {
  if (props.disabled || props.product.hasStock === false) return
  emit('selected', props.product, 1)
}
</script>
